/* eslint-disable max-lines -- data table: enumerates every block-type card (id/label/description/icon/category) */
import type { BlockType, QuizType } from '@/types/blocks'
import type { LucideIcon } from 'lucide-react'
import { messagesNow } from '@/i18n'
import { messages } from './messages'
import {
  Image as ImageIcon,
  CircleDot,
  CheckSquare,
  Link2,
  Heading,
  Type,
  Play,
  Volume2,
  Layers,
  ToggleLeft,
  PenLine,
  SlidersHorizontal,
  ListOrdered,
  BookOpen,
} from 'lucide-react'

export type BlockCardCategory = 'content' | 'interactive'

export type ActiveBlockCardId =
  | 'image'
  | 'video'
  | 'quiz-single'
  | 'quiz-multi'
  | 'yes_no'
  | 'fill_blank'
  | 'match'
  | 'article'
  | 'design'

export type DisabledBlockCardId = 'heading' | 'text' | 'audio' | 'slider' | 'ordering'

export interface ActiveBlockCard {
  id: ActiveBlockCardId
  label: string
  description: string
  icon: LucideIcon
  backendType: BlockType
  category: BlockCardCategory
  multiAnswer?: boolean
  quizType?: QuizType
  /** Бек отдаёт 400 в practical-уроке: у блока нет solve. */
  theoreticalOnly?: true
}

export interface DisabledBlockCard {
  id: DisabledBlockCardId
  label: string
  description: string
  icon: LucideIcon
  category: BlockCardCategory
}

type CardText = { label: string; description: string }
type Prefix<K> = K extends `${infer P}Label` ? P : never
type CardKey = Prefix<keyof typeof messages.ru>

/** Подписи карточки — геттерами: таблица остаётся константой, а текст читается на
 *  языке интерфейса в момент рендера (`messagesNow`). */
const withText = <T extends object>(card: T, key: CardKey): T & CardText =>
  Object.defineProperties(card, {
    label: { enumerable: true, get: () => messagesNow(messages)[`${key}Label`] },
    description: { enumerable: true, get: () => messagesNow(messages)[`${key}Description`] },
  }) as T & CardText

const active = (card: Omit<ActiveBlockCard, keyof CardText>, key: CardKey): ActiveBlockCard =>
  withText(card, key)
const soon = (card: Omit<DisabledBlockCard, keyof CardText>, key: CardKey): DisabledBlockCard =>
  withText(card, key)

export const ACTIVE_BLOCK_CARDS: ActiveBlockCard[] = [
  active(
    {
      id: 'image',
      icon: ImageIcon,
      backendType: 'info',
      category: 'content',
    },
    'image',
  ),
  active(
    {
      id: 'video',
      icon: Play,
      backendType: 'video',
      category: 'content',
      theoreticalOnly: true,
    },
    'video',
  ),
  active(
    {
      id: 'article',
      icon: BookOpen,
      backendType: 'article',
      category: 'content',
    },
    'article',
  ),
  active(
    {
      id: 'design',
      icon: Layers,
      backendType: 'design',
      category: 'content',
      theoreticalOnly: true,
    },
    'design',
  ),
  active(
    {
      id: 'quiz-single',
      icon: CircleDot,
      backendType: 'quiz',
      multiAnswer: false,
      category: 'interactive',
    },
    'quizSingle',
  ),
  active(
    {
      id: 'quiz-multi',
      icon: CheckSquare,
      backendType: 'quiz',
      multiAnswer: true,
      category: 'interactive',
    },
    'quizMulti',
  ),
  active(
    {
      id: 'yes_no',
      icon: ToggleLeft,
      backendType: 'quiz',
      quizType: 'yes_no',
      category: 'interactive',
    },
    'yesNo',
  ),
  active(
    {
      id: 'fill_blank',
      icon: PenLine,
      backendType: 'quiz',
      quizType: 'fill_blank',
      category: 'interactive',
    },
    'fillBlank',
  ),
  active(
    {
      id: 'match',
      icon: Link2,
      backendType: 'match',
      category: 'interactive',
    },
    'match',
  ),
]

export const DISABLED_BLOCK_CARDS: DisabledBlockCard[] = [
  soon(
    {
      id: 'heading',
      icon: Heading,
      category: 'content',
    },
    'heading',
  ),
  soon({ id: 'text', icon: Type, category: 'content' }, 'text'),
  soon({ id: 'audio', icon: Volume2, category: 'content' }, 'audio'),
  soon(
    {
      id: 'slider',
      icon: SlidersHorizontal,
      category: 'interactive',
    },
    'slider',
  ),
  soon(
    {
      id: 'ordering',
      icon: ListOrdered,
      category: 'interactive',
    },
    'ordering',
  ),
]
